import { MenuObjectTypes } from 'enums/menu';

type LegacyMenuOption = {
  _id?: string;
  _type?: string;
  /** Sanity section trees nest options with varying shapes; keep loose for callers. */
  options?: readonly unknown[] | null;
  mainItem?: { _id?: string } | null;
};

function optionContainsItemId(
  option: LegacyMenuOption | null | undefined,
  itemId: string
): boolean {
  if (!option) {
    return false;
  }

  if (option._id === itemId) {
    return true;
  }

  if (option.mainItem?._id === itemId) {
    return true;
  }

  for (const child of option.options ?? []) {
    if (!child || typeof child !== 'object') {
      continue;
    }
    const nested = child as LegacyMenuOption & { option?: { _id?: string } };
    // Picker aspect options: { option: { _id } }
    if (nested.option?._id === itemId) {
      return true;
    }
    if (optionContainsItemId(nested, itemId)) {
      return true;
    }
  }

  return false;
}

/**
 * Finds the nearest Sanity section that lists `itemId` (directly, under a nested
 * section, or under a picker/combo option).
 */
export function findLegacyParentSectionId(
  sections: Iterable<LegacyMenuOption | null | undefined>,
  itemId: string
): string | undefined {
  if (!itemId) {
    return undefined;
  }

  for (const section of sections) {
    if (!section?._id || section._type !== MenuObjectTypes.SECTION) {
      continue;
    }

    if (optionContainsItemId(section, itemId)) {
      // Prefer a nested section when the item lives under one.
      for (const option of section.options ?? []) {
        const nested = option as LegacyMenuOption;
        if (nested?._type === MenuObjectTypes.SECTION && optionContainsItemId(nested, itemId)) {
          const deeper = findLegacyParentSectionId([nested], itemId);
          return deeper ?? nested._id;
        }
      }
      return section._id;
    }
  }

  return undefined;
}
